import {
  isUserAdminView,
  isUserSelfView,
  isUserStaffView,
  LocalUser,
  type LocalUserProps,
  type UserAdminView,
  type UserSelfView,
  type UserStaffView,
  type UserView,
  type View
} from '@/core/domain/users/LocalUser.ts'
import { z } from 'zod'
import {
  LocalUserAdminViewSchema,
  LocalUserPublicViewSchema,
  LocalUserSelfViewSchema,
  LocalUserStaffViewSchema
} from '@/core/infrastructure/schemas/users/core/LocalUserSchema.ts'

/**
 * Mapping View -> ZodSchema
 */
const SCHEMAS: Record<View, z.ZodTypeAny> = {
  public: LocalUserPublicViewSchema,
  staff: LocalUserStaffViewSchema,
  self: LocalUserSelfViewSchema,
  admin: LocalUserAdminViewSchema
}

export class LocalUserFactory {
  /**
   * Constructeur de LocalUser
   * @param data
   * @param view
   */
  static createLocalUser(data: unknown, view: View = 'public') : LocalUser & UserView {
    const schema = SCHEMAS[view]
    const parsed = schema.safeParse(data)
    if (!parsed.success) throw new Error('Invalid data createLocalUser::createLocalUser')
    const user: LocalUserProps = parsed.data satisfies LocalUserProps
    return new LocalUser(user, view)
  }

  /**
   * Constructeur de LocalUserStaffView
   * @param data
   * @param view
   */
  static createUserStaffView(data: unknown, view: View = 'public') : LocalUser & UserStaffView {
    const user = LocalUserFactory.createLocalUser(data, view)
    if (isUserStaffView(user)) return user
    throw new Error('Les données ne sont pas compatibles avec UserStaffView')
  }

  /**
   * Constructeur de LocalUserSelfView
   * @param data
   * @param view
   */
  static createUserSelfView(data: unknown, view: View = 'public') : LocalUser & UserSelfView{
    const user = LocalUserFactory.createLocalUser(data, view)
    if (isUserSelfView(user)) return user
    throw new Error('Les données ne sont pas compatibles avec UserSelfView')
  }

  /**
   * Constructeur de LocalUserAdminView
   * @param data
   * @param view
   */
  static createAdminSelfView(data: unknown, view: View = 'public') : LocalUser & UserAdminView{
    const user = LocalUserFactory.createLocalUser(data, view)
    if (isUserAdminView(user)) return user
    throw new Error('Les données ne sont pas compatibles avec UserAdminView')
  }
}